import React from 'react';
import {BrowserRouter as Router, Route, Routes, Navigate, useLocation} from 'react-router-dom';
import Draw from './pages/draw'
import NoPermission from './pages/404';
import Login from './pages/login'
import {socketService} from "./socket/SocketService";
import {useEffect, useState} from "react";
import {getLocalStorage} from "./util/storage";
import {LoginProps} from "./types/authority";
import {GlobalNotification} from "./services/notification";
import Manage from "./pages/manage";
import {useDispatch, useSelector} from "react-redux";
import {fetchBoardListAsync} from "./store/boardSlice";
import {fetchImagePricingRulesAsync, fetchMemberInfoAsync, setSurveyDialogOpen, setPendingLoginData} from "./store/memberSlice";
import {AppDispatch, RootState} from './store';
import {generateSignature, getUserInfo, IsVisitor} from "./util/author";
import {setCookie} from "./util/cookie";
import AbnormalPage from "./pages/abnormalPage";
import Project from "./pages/Manage/project";
import Home from "./pages/Manage/home";
import {setSnackbarOpen} from "./store/taskSlice";
import {Alert, Snackbar} from "@mui/material";
import { ThemeModeProvider } from '@naro/krene-theme';
import SurveyDialog from "./component/surveyDialog";
import {setLocalStorage} from "./util/storage";
import ReactMarkdown from "react-markdown";
import {useTranslation} from "react-i18next";
import RemoteInspireBoardComponent from "./components/RemoteInspireBoardComponent";
import {setUserInfo} from "./store/userSlice";
import TipsDialog from "./component/tipsDialog";
import UnreadNotificationDialog from "./component/tipsDialog/unreadNotification";
import Invoice from "./pages/Invoice/index";
import NewActive from "./pages/NewActive";
import NewActiveB from "./pages/NewActive/indexB";
import { initUserConfig } from './config/userConfig';
import {
    captureRegistrationAttributionVisit,
    attributionQuerySummary,
    setSharedLoginState
} from './util/registrationAttribution';
import {setMatomoUserId, trackPageView} from "./hooks/useMatomoTrackingPoint";

// https://qa9283es425.vicp.fun/draw/?clientId=visitor-m8pptjjx-11jkwnx6
// https://qa9283es425.vicp.fun/draw/?clientId=visitor-m8pptjjx-11jkwnx6
export default function App() {

    const base_socket_url = `${process.env.REACT_APP_SOCKET_IO_URL}`;

    const snackbarOpen = useSelector((state: RootState) => state.task.snackbarOpen);
    const userInfo = useSelector((state: RootState) => state.user.userInfo);
    const surveyDialogOpen = useSelector((state: RootState) => state.member.surveyDialogOpen);
    const pendingLoginData = useSelector((state: RootState) => state.member.pendingLoginData);
    const searchParams = new URLSearchParams(window.location.search);
    const cid = searchParams.get('clientId');
    const entrySearch = window.location.search;

    const [clientId, setClientId] = useState<string | null>(cid);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    const dispatch: AppDispatch = useDispatch()

    const handleSetUserInfo = (data: LoginProps | null) => {
        dispatch(setUserInfo(data))
    }

    const { t } = useTranslation()

    const useInfoStr = getLocalStorage('userInfo');
    const localUserInfo = useInfoStr ? JSON.parse(useInfoStr) as LoginProps : null;

    const updateSignature = (userId: string, userName: string) => {
        generateSignature(process.env.REACT_APP_SHARED_KEY || '', {
            userId: userId,
            username: userName,
            timestamp: Date.now(),
            permission: ['read', 'write']
        }).then(signature=>{
            if (signature) setCookie('s', signature);
        })
    }

    useEffect(() => {
        if (localUserInfo) {
            setSharedLoginState(true, localUserInfo.uuid);
            handleSetUserInfo(localUserInfo);
            dispatch(fetchBoardListAsync())
            setInterval(()=>{
                updateSignature(localUserInfo.uuid, localUserInfo.userName)
            }, 3600000)
            updateSignature(localUserInfo.uuid, localUserInfo.userName)
        }
    }, []);

    useEffect(() => {
        dispatch(fetchImagePricingRulesAsync())
        if (userInfo) {
            initUserConfig(dispatch).then();
        }
    }, [userInfo]);

    // 同步 Matomo 原生 userId
    useEffect(() => {
        setMatomoUserId(userInfo?.uuid);
    }, [userInfo?.uuid]);

    return (
        <ThemeModeProvider>
        <Router basename={process.env.PUBLIC_URL}>
            <RegistrationAttributionRouteTracker />
            <GlobalNotification />
            <TipsDialog />
            <UnreadNotificationDialog />
            <Snackbar
                open={snackbarOpen.open}
                onClose={() => dispatch(setSnackbarOpen({open: false, message: ''}))}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                sx={{
                    '& .MuiAlert-root': {
                        alignItems: 'center',
                        padding: '6px 16px'
                    },
                    '& .MuiAlert-icon': {
                        padding: 0,
                        marginRight: '8px'
                    },
                    '& .MuiAlert-action': {
                        padding: 0,
                        marginLeft: '8px'
                    },
                    '& .MuiAlert-message': {
                        padding: 0,
                    }
                }}
            >
                <Alert
                    severity="error"
                    onClose={() => dispatch(setSnackbarOpen({open: false, message: ''}))}
                    sx={{
                        width: 'auto',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <ReactMarkdown children={snackbarOpen.message} />

                </Alert>
            </Snackbar>
            <Routes>
                <Route path="/" element={userInfo || useInfoStr || IsVisitor() ?
                    <Draw userInfo={userInfo || localUserInfo} handleSetUserInfo={handleSetUserInfo}/> :
                    <Navigate to={`/dashboard/home${entrySearch || ''}`} replace />} />
                <Route path="/404" element={<NoPermission />} />
                <Route path="/invoice/:outTradeNo" element={<Invoice />} />
                <Route path="/active" element={<NewActive />} />
                <Route path="/active-b" element={<NewActiveB />} />
                <Route path="/dashboard" element={<Manage />}>
                    <Route index element={<Project />} />
                    <Route path="home" element={<Home />} />
                </Route>
                <Route path="/abnormalPage" element={<AbnormalPage />} />
                <Route path="/login" element={
                    userInfo ? <Navigate to="/dashboard" replace /> : <Login handleSetUserInfo={handleSetUserInfo} />
                } />
                {/* 捕获所有未匹配的路由，跳转到首页 */}
                <Route path="*" element={<Navigate to="/dashboard/home" replace />} />
            </Routes>
            <SurveyDialog open={surveyDialogOpen} onClose={() => dispatch(setSurveyDialogOpen(false))} token={pendingLoginData?.token} uuid={pendingLoginData?.uuid} switchFrom="home" onSubmitted={() => {
                if (pendingLoginData) {
                    setLocalStorage('userInfo', JSON.stringify(pendingLoginData));
                    dispatch(setUserInfo(pendingLoginData));
                    generateSignature(process.env.REACT_APP_SHARED_KEY || '', {
                        userId: pendingLoginData.uuid,
                        username: pendingLoginData.userName,
                        timestamp: Date.now(),
                        permission: ['read', 'write']
                    }).then(signature => {
                        if (signature) setCookie('s', signature);
                    });
                    dispatch(fetchMemberInfoAsync());
                    dispatch(setPendingLoginData(null));
                    dispatch(setSurveyDialogOpen(false));
                }
            }}/>
        </Router>
        </ThemeModeProvider>
    );
}

function RegistrationAttributionRouteTracker() {
    const location = useLocation();
    const previousHrefRef = React.useRef<string>('');

    useEffect(() => {
        const currentHref = window.location.href;
        const loginStatus = getUserInfo() ? 'logged_in' : 'anonymous';
        const referrer = previousHrefRef.current || document.referrer || '';
        const attributionState = captureRegistrationAttributionVisit(loginStatus, {
            url: currentHref,
            referrer,
        });
        if (previousHrefRef.current && previousHrefRef.current !== currentHref) {
            trackPageView({
                load_time: performance.now(),
                page_id: currentHref,
                referrer,
                utm: attributionQuerySummary(attributionState),
                login_status: loginStatus,
            });
        }
        previousHrefRef.current = currentHref;
    }, [location.pathname, location.search]);

    return null;
}
